import React, { useEffect, useMemo, useState } from 'react'
import {
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaFileDownload,
} from 'react-icons/fa'
import { CSVLink } from 'react-csv'
import { useGOMenuSearch } from '@components/groupOrders/UniversalMenuPreview/useGOMenuSearch'
import Checkbox from '@components/common/form/Checkbox'
import Dropdown from '@components/common/form/Dropdown'
import NumberInputWithNull from '@components/common/form/NumberInputWithNull'
import VendorBlacklist from '@components/groupOrders/VendorBlacklist'
import MenuItemBlacklist from '@components/groupOrders/MenuItemBlacklist'
import { Label } from '@res/styledComponents/index'
import { AutocompleteInput } from '@components/common/form'
import {
  ClientSettings,
  ItemBlacklistEntry,
  VendorBlacklistEntry,
  VendorGroup,
  PreviewMenu,
  PreviewMenuItem,
  SearchUniversalPreviewMenusReq,
} from '@types'
import { ClosedBlacklistedBtn } from '@components/groupOrders/VendorBlacklist'
import Loader from '@components/common/Loader'
import { ExportButton } from '@components/groupOrders/SchedulePlanner/MenusFinder/GroupOrderMenusFinder'
import { vendorGroupsToCSV } from '@components/groupOrders/UniversalMenuPreview/utils'
import {
  GROUP_ORDER_SUBSIDY_TYPE_FLAT_PRICE,
  GROUP_ORDER_SUBSIDY_TYPE_PERCENT,
  GroupOrderSubsidyTypes,
} from '../constants'
import { parseMilitaryTimeToAMPM } from '@utils'
import { formatCutoff } from '@utils/groupOrderUtils'

interface ViewSettings {
  expandedVendors: Set<string>
  expandedMenus: Set<string>
}

interface UniversalMenuPreviewProps {
  hqId: number
  searchGOMenus: (params: SearchUniversalPreviewMenusReq) => Promise<any>
  searchClientSettings: () => void
  displayFailureMessage: (mssg: string) => void
  dismissInfoModal: () => void
  showCustomModal: any
}

const UniversalMenuPreview: React.FC<UniversalMenuPreviewProps> = ({
  hqId,
  searchClientSettings,
  searchGOMenus,
  showCustomModal,
  displayFailureMessage,
  dismissInfoModal,
}) => {
  const [selectedClientSettings, setSelectedClientSettings] =
    useState<ClientSettings | null>()
  const [vendorBlacklist, setVendorBlacklist] = useState<
    VendorBlacklistEntry[]
  >([])
  const [menuItemBlacklist, setMenuItemBlacklist] = useState<
    ItemBlacklistEntry[]
  >([])
  const [search, setSearch] = useState<string>('')
  const [viewSettings, setViewSettings] = useState<ViewSettings>({
    expandedMenus: new Set(),
    expandedVendors: new Set(),
  })
  const [initialMenusLoaded, setInitialMenusLoaded] = useState(false)

  const blacklistedItemIds = useMemo(
    () =>
      new Set(menuItemBlacklist.filter((e) => e.active).map((e) => e.itemId)),
    [menuItemBlacklist],
  )
  const blacklistedVendorIds = useMemo(
    () =>
      new Set(vendorBlacklist.filter((e) => e.active).map((e) => e.vendorId)),
    [vendorBlacklist],
  )

  const {
    loadingMenus,
    menus,
    filters,

    resetFilters,
    setEnableSides,
    setEnableModifications,
    setFlatPriceMarginMin,
    setSubisdyType,
    setSubsidyAmt,
    setDayOfWeek,
    setMaxPriceLimit,
  } = useGOMenuSearch({
    hqId,
    blacklistedItemIds,
    blacklistedVendorIds,
    search,
    displayFailureMessage,
    searchGOMenus,
  })

  useEffect(() => {
    if (!initialMenusLoaded && menus.length > 0) {
      const expandedVendorIds = menus.map((m) => m.vendorId)
      const expandedMenuIds = menus.flatMap((m) => m.menus.map((m) => m.id))
      setViewSettings({
        expandedMenus: new Set(expandedMenuIds),
        expandedVendors: new Set(expandedVendorIds),
      })
      setInitialMenusLoaded(true)
    }
  }, [menus, initialMenusLoaded])

  const csvData = vendorGroupsToCSV(menus)
  const csvFilename = `menus-${new Date().toISOString().split('T')[0]}.csv`

  const toggleVendor = (vendorId: string) => {
    const newExpanded = new Set(viewSettings.expandedVendors)
    if (newExpanded.has(vendorId)) {
      newExpanded.delete(vendorId)
    } else {
      newExpanded.add(vendorId)
    }
    setViewSettings((prev) => ({ ...prev, expandedVendors: newExpanded }))
  }

  const toggleMenu = (menuId: string) => {
    const newExpanded = new Set(viewSettings.expandedMenus)
    if (newExpanded.has(menuId)) {
      newExpanded.delete(menuId)
    } else {
      newExpanded.add(menuId)
    }
    setViewSettings((prev) => ({ ...prev, expandedMenus: newExpanded }))
  }

  const expandAllVendors = () => {
    setViewSettings((prev) => ({
      ...prev,
      expandedVendors: new Set(menus.map((m) => m.vendorId)),
    }))
  }

  const collapseAllVendors = () => {
    setViewSettings((prev) => ({ ...prev, expandedVendors: new Set() }))
  }

  const expandAllMenus = () => {
    const expandedMenuIds = menus.flatMap((m) => m.menus.map((m) => m.id))
    setViewSettings((prev) => ({
      ...prev,
      expandedMenus: new Set(expandedMenuIds),
    }))
  }

  const collapseAllMenus = () => {
    setViewSettings((prev) => ({ ...prev, expandedMenus: new Set() }))
  }

  const handleSelectClientSettings = (cs: ClientSettings) => {
    setSelectedClientSettings(cs)
    if (cs.universalMenuConfig) {
      const { subsidySettings } = cs
      const {
        enableSides,
        enableModifications,
        flatPriceMarginMinimum,
        maxItemPriceLimit,
        vendorBlacklist,
        menuItemBlacklist,
      } = cs.universalMenuConfig
      setEnableSides(enableSides)
      setEnableModifications(enableModifications)
      setMaxPriceLimit(maxItemPriceLimit)
      setVendorBlacklist(vendorBlacklist)
      setMenuItemBlacklist(menuItemBlacklist)
      if (flatPriceMarginMinimum) {
        setSubisdyType(GROUP_ORDER_SUBSIDY_TYPE_FLAT_PRICE)
        setFlatPriceMarginMin(flatPriceMarginMinimum)
      }
      const activeSubs = subsidySettings.subsidies.filter((s) => s.isActive)
      let filteredSubsidy

      if (filters.dayOfWeek != null) {
        const selectedDay = filters.dayOfWeek
        filteredSubsidy = activeSubs.find((s) => s.days?.includes(selectedDay))
      } else {
        filteredSubsidy = activeSubs[0]
      }
      if (filteredSubsidy) {
        if (!flatPriceMarginMinimum) {
          setSubisdyType(filteredSubsidy.subsidyType)
        }
        const { perOrderSubsidy } = filteredSubsidy
        if (perOrderSubsidy) {
          setSubsidyAmt(Number(perOrderSubsidy))
        }
      }
    }
  }

  const handleClearClientSettings = () => {
    setSelectedClientSettings(undefined)
    setVendorBlacklist([])
    setMenuItemBlacklist([])
    resetFilters()
  }

  const onOpenVendorsBlacklist = () => {
    showCustomModal({
      title: 'Vendor Blacklist',
      content: (
        <VendorBlacklist
          list={vendorBlacklist}
          editable={false}
          close={dismissInfoModal}
        />
      ),
    })
  }

  const onOpenMenuItemsBlacklist = () => {
    showCustomModal({
      title: 'Menu Items Blacklist',
      content: (
        <MenuItemBlacklist
          list={menuItemBlacklist}
          editable={false}
          close={dismissInfoModal}
        />
      ),
    })
  }

  const vendorBlacklistSet = new Set(
    vendorBlacklist.filter((e) => e.active).map((e) => e.vendorId),
  )
  const numberOfMenus = menus.flatMap((m) => m.menus).length

  return (
    <div className="flex gap-6">
      {/* <SettingsSidebar /> */}
      <div className="w-80 bg-white rounded-lg shadow-md border border-gray-300 border-solid p-6 h-fit max-w-xs max-h-850 ">
        <div className="space-y-6 pb-6">
          <div>
            <h3 className="font-semibold text-lg mb-2">Client Settings</h3>
            <p className="text-sm text-gray-600 mb-4">
              Select a client to autopopulate settings, then edit these settings
              to see how they affect menu options.
            </p>
          </div>
          <div className="flex flex-col space-y-2">
            <Label>Select Client Settings </Label>
            <AutocompleteInput
              value={selectedClientSettings?.name}
              loaderFunction={searchClientSettings}
              onSelect={(cs: ClientSettings) => handleSelectClientSettings(cs)}
              onClear={() => handleClearClientSettings()}
            />
          </div>
        </div>

        <div className="space-y-4 pb-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              label="Enable Sides"
              id="enable-sides"
              checked={filters.enableSides}
              onChange={() => setEnableSides(!filters.enableSides)}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              label="Enable Modifications"
              id="enable-modifications"
              checked={filters.enableMods}
              onChange={() => setEnableModifications(!filters.enableMods)}
            />
          </div>
        </div>

        {/* Subsidy */}
        <div className="space-y-4">
          <Dropdown
            label="Subsidy Settings"
            value={filters.subsidyType}
            onChange={(e) => {
              let { value } = e.target
              if (!value) {
                value = null
              }
              setSubisdyType(value)
            }}
          >
            <option value={''}></option>
            {GroupOrderSubsidyTypes.map((sT) => (
              <option key={sT} value={sT}>
                {sT}
              </option>
            ))}
          </Dropdown>
          {filters.subsidyType && (
            <div>
              <Label>Subsidy Amount</Label>
              <NumberInputWithNull
                value={filters.subsidyAmt}
                onChange={(val: number | null) => setSubsidyAmt(val)}
                className="h-10"
                min={0}
                step={1}
                max={
                  filters.subsidyType == GROUP_ORDER_SUBSIDY_TYPE_PERCENT
                    ? 100
                    : undefined
                }
                asPercent={
                  filters.subsidyType == GROUP_ORDER_SUBSIDY_TYPE_PERCENT
                }
              />
            </div>
          )}
          <div>
            <Label>Day of Week</Label>
            <Dropdown
              value={filters.dayOfWeek}
              onChange={(e) => setDayOfWeek(e.target.value)}
            >
              <option></option>
              <option value={1}>Monday</option>
              <option value={2}>Tuesday</option>
              <option value={3}>Wednesday</option>
              <option value={4}>Thursday</option>
              <option value={5}>Friday</option>
              <option value={6}>Saturday</option>
              <option value={7}>Sunday</option>
            </Dropdown>
          </div>
          {filters.subsidyType == GROUP_ORDER_SUBSIDY_TYPE_FLAT_PRICE && (
            <div>
              <Label>Flat Price Margin Minimum</Label>
              <NumberInputWithNull
                value={filters.flatPriceMarginMin}
                className="h-10"
                min={0}
                step={1}
                onChange={(val: number | null) => setFlatPriceMarginMin(val)}
                asPercent={true}
              />
            </div>
          )}
          <div>
            <Label>Max Price Limit ($)</Label>
            <NumberInputWithNull
              value={filters.maxPriceLimit}
              onChange={(val: number | null) => setMaxPriceLimit(val)}
              min={0}
            />
          </div>
          <div className="flex flex-col space-y-4 w-full">
            <ClosedBlacklistedBtn
              label="Blacklisted Vendors"
              listCount={vendorBlacklist.filter((e) => e.active).length}
              toggleOpen={onOpenVendorsBlacklist}
            />
            <ClosedBlacklistedBtn
              label="Blacklisted Menu Items"
              listCount={menuItemBlacklist.filter((e) => e.active).length}
              toggleOpen={onOpenMenuItemsBlacklist}
            />
          </div>
        </div>
      </div>
      <div className="flex-1">
        {/* Settings Header */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4 w-1/3">
              {menus.length > 0 && (
                <CSVLink
                  data={csvData}
                  filename={csvFilename}
                  style={{
                    textDecoration: 'none',
                    width: '100%',
                    justifyItems: 'center',
                  }}
                  target="_blank"
                >
                  <ExportButton>
                    <FaFileDownload />
                    Export to CSV
                  </ExportButton>
                </CSVLink>
              )}
            </div>

            <div className="flex flex-row items-center justify-start gap-2 border-solid border-gray-400 border rounded-md p-2">
              <FaSearch className=" text-gray-400" />
              <input
                placeholder="Search menus, vendors, items..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-64"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <button
                className="flex flex-row items-center py-2 px-1 border-solid border-gray-600 border rounded-lg hover:bg-gray-200 font-semibold"
                onClick={expandAllVendors}
              >
                <FaChevronDown className="h-4 w-4 mx-1" />
                Expand All Vendors
              </button>
              <button
                className="flex flex-row items-center py-2 px-1 border-solid border-gray-600 border rounded-lg hover:bg-gray-200 font-semibold"
                onClick={collapseAllVendors}
              >
                <FaChevronUp className="h-4 w-4 mx-1" />
                Collapse All Vendors
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button
                className="flex flex-row items-center py-2 px-1 border-solid border-gray-600 border rounded-lg hover:bg-gray-200 font-semibold"
                onClick={expandAllMenus}
              >
                <FaChevronDown className="h-4 w-4 mx-1" />
                Expand All Menus
              </button>
              <button
                className="flex flex-row items-center py-2 px-1 border-solid border-gray-600 border rounded-lg hover:bg-gray-200 font-semibold"
                onClick={collapseAllMenus}
              >
                <FaChevronUp className="h-4 w-4 mx-1" />
                Collapse All Menus
              </button>
            </div>

            <div className="text-sm text-gray-600">
              {numberOfMenus} Menus Found
            </div>
          </div>
        </div>

        {/* Table  */}
        <div className="bg-white rounded-lg shadow-md border">
          <div className="overflow-x-auto rounded-lg">
            <table className="w-full">
              <thead className="bg-teal-500 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Item Name</th>
                  <th className="px-4 py-3 text-left font-medium">Meal Type</th>
                  <th className="px-4 py-3 text-left font-medium">Has Mods</th>
                  <th className="px-4 py-3 text-left font-medium">
                    Customizable Alternate Name
                  </th>
                  <th className="px-4 py-3 text-left font-medium">
                    Dietary Tags
                  </th>
                  <th className="px-4 py-3 text-left font-medium">
                    Vendor Price
                  </th>
                  <th className="px-4 py-3 text-left font-medium">
                    Client Price
                  </th>
                  <th className="px-4 py-3 text-left font-medium">Margin</th>
                  <th>Subsidy Covered</th>
                  <th>Employee Pays</th>
                </tr>
              </thead>
              <tbody>
                {loadingMenus ? (
                  <Loader />
                ) : (
                  menus.map((vendorGroup) => (
                    <VendorRow
                      key={vendorGroup.vendorId}
                      vendorGroup={vendorGroup}
                      isExpanded={viewSettings.expandedVendors.has(
                        vendorGroup.vendorId,
                      )}
                      vendorBlacklisted={vendorBlacklistSet.has(
                        vendorGroup.vendorId,
                      )}
                      onToggle={() => toggleVendor(vendorGroup.vendorId)}
                      onMenuToggle={toggleMenu}
                      expandedMenus={viewSettings.expandedMenus}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UniversalMenuPreview

interface VendorRowProps {
  isExpanded: boolean
  expandedMenus: Set<string>
  vendorGroup: VendorGroup
  vendorBlacklisted: boolean

  onToggle: () => void
  onMenuToggle: (menuId: string) => void
}

const VendorRow: React.FC<VendorRowProps> = ({
  vendorGroup,
  vendorBlacklisted,
  isExpanded,
  onToggle,
  onMenuToggle,
  expandedMenus,
}) => {
  return (
    <>
      <tr
        className={`border-b border-gray-400 border-solid hover:bg-gray-200 ${
          vendorBlacklisted ? 'opacity-50 bg-gray-400' : ''
        }`}
      >
        <td className="px-4 py-3" colSpan={10}>
          <div className="flex items-center gap-2">
            <button onClick={onToggle}>
              {isExpanded ? (
                <FaChevronUp className="h-4 w-4" />
              ) : (
                <FaChevronDown className="h-4 w-4" />
              )}
            </button>
            <span className="font-medium">{vendorGroup.vendorName}</span>
            <span className="text-gray-600 text-sm ml-4">
              Earliest Pickup:{' '}
              {vendorGroup.earliestPickup
                ? parseMilitaryTimeToAMPM(vendorGroup.earliestPickup)
                : 'N/A'}{' '}
              | Max Items/Day: {vendorGroup.maxItemsPerDay || 'N/A'}
            </span>
          </div>
        </td>
      </tr>

      {isExpanded &&
        vendorGroup.menus.map((menu) => (
          <MenuRow
            key={menu.id}
            menu={menu}
            isExpanded={expandedMenus.has(menu.id)}
            onToggle={() => onMenuToggle(menu.id)}
            vendorBlacklisted={vendorBlacklisted}
          />
        ))}
    </>
  )
}

interface MenuRowProps {
  menu: PreviewMenu
  isExpanded: boolean
  vendorBlacklisted: boolean
  onToggle: () => void
}

const MenuRow: React.FC<MenuRowProps> = ({
  menu,
  isExpanded,
  onToggle,
  vendorBlacklisted,
}) => {
  const menuAvailable = menu.availability.available && !vendorBlacklisted

  return (
    <>
      <tr
        className={`border-b border-gray-400 border-solid bg-gray-100 hover:bg-gray-300 ${
          !menuAvailable ? 'opacity-25 bg-gray-100' : ''
        }`}
      >
        <td className="px-4 py-2 pl-12" colSpan={10}>
          <div className="flex items-center gap-2">
            <button onClick={onToggle}>
              {isExpanded ? (
                <FaChevronUp className="h-4 w-4" />
              ) : (
                <FaChevronDown className="h-4 w-4" />
              )}
            </button>
            <span className="font-medium text-sm">{menu.name}</span>
            <span className="text-gray-600 text-sm ml-4">
              Cutoff:{' '}
              {formatCutoff(
                menu.groupOrderCutoffDays,
                menu.groupOrderCutoffTime,
              )}{' '}
              |{' '}
              <strong>
                {menu.isUniversal ? 'Universal' : 'Non-Universal'}
              </strong>{' '}
              | Avg Margin: {menu.margin.toFixed(2)}%
            </span>
          </div>
        </td>
      </tr>

      {isExpanded &&
        menu.allMenuItems.map((item: PreviewMenuItem, index: number) => {
          const {
            availability,
            hasMods,
            chefPrice,
            tagsDietaryPrefList,
            mealType,
            name,
            price,
            margin,
            subsidyCovered,
            employeePays,

            customizedAlternative,
          } = item

          const available = availability.available && menuAvailable
          const dietaryPrefs = (tagsDietaryPrefList || '').split(',')

          return (
            <tr
              key={index}
              className={`border-b border-gray-300 border-solid bg-gray-50 ${
                !available ? 'opacity-25 bg-gray-100' : ''
              } `}
            >
              <td className="px-4 py-2 pl-20">
                <div className="text-sm font-medium">{name}</div>
              </td>
              <td className="px-4 py-2 text-sm">{mealType}</td>
              <td className="px-4 py-2 text-sm">{hasMods ? 'Yes' : 'No'}</td>
              <td className="px-4 py-2 text-sm">
                <div className="flex gap-1 flex-wrap">
                  <div
                    className={`p-1 rounded-lg ${
                      customizedAlternative && 'bg-green-400 text-white'
                    } flex items-center`}
                  >
                    {customizedAlternative || 'N/A'}
                  </div>
                </div>
              </td>
              <td className="px-4 py-2">
                <div className="flex gap-1 flex-wrap">
                  {dietaryPrefs.map((tag: string) => (
                    <div
                      key={tag}
                      className="text-xs p-1 rounded-lg bg-gray-200 flex items-center"
                    >
                      {tag}
                    </div>
                  ))}
                </div>
              </td>
              <td className="px-4 py-2 text-sm">
                ${(chefPrice || 0).toFixed(2)}
              </td>
              <td className="px-4 py-2 text-sm">${(price || 0).toFixed(2)}</td>
              <td className="px-4 py-2 text-sm">{(margin || 0).toFixed(2)}%</td>
              <td>${(subsidyCovered || 0).toFixed(2)}</td>
              <td>${(employeePays || 0).toFixed(2)}</td>
            </tr>
          )
        })}
    </>
  )
}
